/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * certificateMap
 * --------------
 * The real map for a certificate: the OpenStreetMap tiles around the
 * activity's course (the same map the event page shows), with the course,
 * Start and Finish drawn on it — or, for a venue activity, the map around the
 * venue with a pin. Built in the browser on a canvas and handed to the
 * template as one picture, `{{ map_image }}` (a data: URL), so a template
 * only needs <img src="{{ map_image }}"> in its map area.
 *
 * Reused by every certificate render: the event wizard previews, the
 * Generate / View dialog, the PDF / PNG export, and the participant's
 * download (Phase 5). Cached per course, so one course is built once.
 *
 * When the map cannot be built (no network, tiles refused) the certificate
 * falls back to the template's styled background with the course drawn on it.
 */

import { useQuery } from '@tanstack/react-query';

export const TILE_URL = (z, x, y) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
export const ATTRIBUTION = '© OpenStreetMap contributors';

/** The picture's size: portrait, like the templates' map panels, at 2× for print. */
export const MAP_SIZE = { width: 600, height: 794, scale: 2 };
const TILE = 256;
const MAX_ZOOM = 17;
const MIN_ZOOM = 3;
const VENUE_ZOOM = 16;
// The course fills at most this share of the picture, so it stays clear of
// the edges when a template crops the picture to its panel (object-fit: cover).
const FIT = { width: 0.62, height: 0.66 };

const ORANGE = '#FF7A00';
const ATTRIBUTION_X = 120;

/* ------------------------------------------------------------- geometry --- */

/** Web Mercator: longitude/latitude → world pixels at zoom z (OSM's projection). */
export function project(lng, lat, z) {
  const world = TILE * 2 ** z;
  const sin = Math.sin((Math.max(-85.0511, Math.min(85.0511, lat)) * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * world,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * world,
  };
}

/** The course's [lng, lat] points, from a GeoJSON LineString (object or JSON text). */
export function coursePoints(routeLine) {
  let line = routeLine;
  if (typeof line === 'string') {
    try { line = JSON.parse(line); } catch { return null; }
  }
  const coords = line && line.type === 'LineString' && Array.isArray(line.coordinates)
    ? line.coordinates.filter((c) => Array.isArray(c) && Number.isFinite(+c[0]) && Number.isFinite(+c[1])).map((c) => [+c[0], +c[1]])
    : [];
  return coords.length >= 2 ? coords : null;
}

/** A venue point as [lng, lat], from { lat, lng } or a GeoJSON Point. */
export function venuePoint(point) {
  let p = point;
  if (typeof p === 'string') {
    try { p = JSON.parse(p); } catch { return null; }
  }
  if (p && Number.isFinite(+p.lat) && Number.isFinite(+p.lng) && (p.lat !== '' && p.lng !== '')) return [+p.lng, +p.lat];
  if (p && p.type === 'Point' && Array.isArray(p.coordinates)) return [+p.coordinates[0], +p.coordinates[1]];
  return null;
}

/**
 * The view for a course: the highest zoom at which it fits the safe area,
 * centred on it. A single point (a venue) uses a fixed street-level zoom.
 */
export function mapView(points, size = MAP_SIZE) {
  if (points.length === 1) {
    const c = project(points[0][0], points[0][1], VENUE_ZOOM);
    return { zoom: VENUE_ZOOM, centerX: c.x, centerY: c.y };
  }
  for (let zoom = MAX_ZOOM; zoom >= MIN_ZOOM; zoom -= 1) {
    const px = points.map(([lng, lat]) => project(lng, lat, zoom));
    const xs = px.map((p) => p.x); const ys = px.map((p) => p.y);
    const w = Math.max(...xs) - Math.min(...xs); const h = Math.max(...ys) - Math.min(...ys);
    if (w <= size.width * FIT.width && h <= size.height * FIT.height) {
      return { zoom, centerX: (Math.max(...xs) + Math.min(...xs)) / 2, centerY: (Math.max(...ys) + Math.min(...ys)) / 2 };
    }
  }
  const c = project(points[0][0], points[0][1], MIN_ZOOM);
  return { zoom: MIN_ZOOM, centerX: c.x, centerY: c.y };
}

/** The tiles that cover the picture, and where each one is drawn. */
export function tilesFor(view, size = MAP_SIZE) {
  const left = view.centerX - size.width / 2;
  const top = view.centerY - size.height / 2;
  const count = 2 ** view.zoom;
  const tiles = [];
  for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + size.height) / TILE); ty += 1) {
    if (ty < 0 || ty >= count) continue;
    for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + size.width) / TILE); tx += 1) {
      tiles.push({ url: TILE_URL(view.zoom, ((tx % count) + count) % count, ty), dx: tx * TILE - left, dy: ty * TILE - top });
    }
  }
  return { tiles, left, top };
}

/* -------------------------------------------------------------- drawing --- */

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('A map tile could not be loaded.'));
    img.src = url;
  });
}

function pin(ctx, x, y, color, r) {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.bezierCurveTo(x - r * 0.9, y - r * 1.3, x - r, y - r * 1.6, x - r, y - r * 2);
  ctx.arc(x, y - r * 2, r, Math.PI, 0, false);
  ctx.bezierCurveTo(x + r, y - r * 1.6, x + r * 0.9, y - r * 1.3, x, y);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 6;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#FFFFFF';
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y - r * 2, r * 0.38, 0, Math.PI * 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.restore();
}

// The "Paper" look: the real streets in the certificate's own two colours.
// Light parts of the map take the paper colour and dark parts the street
// colour, so none of OpenStreetMap's greens, pinks or blues are left.
export const MAP_PAPER = [252, 251, 249]; // #FCFBF9, the certificate's paper
export const MAP_STREETS = [189, 177, 162]; // #BDB1A2, soft taupe
const MAP_GAMMA = 0.8; // < 1 lifts the mid tones, so the map stays light

/** One pixel's luminance (0–255) → its paper-tone colour. */
export function paperToneOf(r, g, b) {
  const t = ((0.299 * r + 0.587 * g + 0.114 * b) / 255) ** MAP_GAMMA;
  return MAP_STREETS.map((dark, c) => Math.round(dark + (MAP_PAPER[c] - dark) * t));
}

function paperTone(ctx, canvas) {
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const px = image.data;
  for (let i = 0; i < px.length; i += 4) {
    const [r, g, b] = paperToneOf(px[i], px[i + 1], px[i + 2]);
    px[i] = r; px[i + 1] = g; px[i + 2] = b;
  }
  ctx.putImageData(image, 0, 0);
}

function label(ctx, text, x, y) {
  ctx.save();
  ctx.font = '700 15px Montserrat, "Hanken Grotesk", Arial, sans-serif';
  const w = ctx.measureText(text).width + 16;
  ctx.fillStyle = 'rgba(255,255,255,0.94)';
  ctx.beginPath();
  ctx.roundRect ? ctx.roundRect(x, y - 13, w, 26, 13) : ctx.rect(x, y - 13, w, 26);
  ctx.fill();
  ctx.fillStyle = '#14110D';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + 8, y + 1);
  ctx.restore();
}

/**
 * Builds the picture: tiles, then the course (a soft halo and the line) with
 * Start and Finish pins, or a venue pin with its name; the attribution
 * OpenStreetMap requires in the corner. Resolves to a JPEG data: URL.
 */
export async function buildCertificateMap({ routeLine, venue, venueName } = {}) {
  const course = coursePoints(routeLine);
  const place = course ? null : venuePoint(venue);
  if (!course && !place) return null;
  const points = course || [place];
  const view = mapView(points);
  const { tiles, left, top } = tilesFor(view);
  const images = await Promise.all(tiles.map((t) => loadImage(t.url)));

  const { width, height, scale } = MAP_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#F2EFE9';
  ctx.fillRect(0, 0, width, height);
  images.forEach((img, i) => ctx.drawImage(img, tiles[i].dx, tiles[i].dy, TILE, TILE));
  paperTone(ctx, canvas);

  const at = ([lng, lat]) => {
    const p = project(lng, lat, view.zoom);
    return [p.x - left, p.y - top];
  };

  if (course) {
    const px = course.map(at);
    const path = () => {
      ctx.beginPath();
      px.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    };
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    path(); ctx.strokeStyle = 'rgba(255,122,0,0.25)'; ctx.lineWidth = 16; ctx.stroke();
    path(); ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 8; ctx.stroke();
    path(); ctx.strokeStyle = ORANGE; ctx.lineWidth = 5; ctx.stroke();
    const [sx, sy] = px[0];
    const [ex, ey] = px[px.length - 1];
    pin(ctx, ex, ey, '#E03A3A', 11);
    pin(ctx, sx, sy, '#1E9E58', 11);
    label(ctx, 'Finish', ex + 14, ey - 22);
    label(ctx, 'Start', sx + 14, sy - 22);
  } else {
    const [x, y] = at(place);
    pin(ctx, x, y, ORANGE, 14);
    if (venueName) label(ctx, String(venueName), x + 18, y - 28);
  }

  // OpenStreetMap's required credit, near the top and in from the left: the
  // templates fade the map's left edge and put a results card top right and
  // stripes bottom right, so this is the spot that stays readable.
  ctx.save();
  ctx.font = '500 10px Arial, sans-serif';
  const aw = ctx.measureText(ATTRIBUTION).width + 10;
  ctx.fillStyle = 'rgba(255,255,255,0.88)';
  ctx.fillRect(ATTRIBUTION_X, 6, aw, 16);
  ctx.fillStyle = '#333333';
  ctx.textBaseline = 'middle';
  ctx.fillText(ATTRIBUTION, ATTRIBUTION_X + 5, 14);
  ctx.restore();

  return canvas.toDataURL('image/jpeg', 0.9);
}

const cache = new Map();

/** The same picture for the same course or venue, built once per visit. */
export function loadCertificateMap(course = {}) {
  const key = JSON.stringify([coursePoints(course.routeLine), venuePoint(course.venue), course.venueName || '']);
  if (!cache.has(key)) {
    const job = buildCertificateMap(course);
    cache.set(key, job);
    job.catch(() => cache.delete(key));
  }
  return cache.get(key);
}

/**
 * The certificate values with the map added: `map_image` when the picture was
 * built, and the flags a template uses for its styled fallback —
 * `styled_route` (draw the course on the styled background) and
 * `styled_venue` (a pin with the venue's name) — only when there is no map.
 */
export function withCertificateMap(values, mapImage) {
  const map = mapImage || '';
  return {
    ...values,
    map_image: map,
    styled_route: !map && values.route_path ? 'yes' : '',
    styled_venue: !map && !values.route_path && values.venue_name ? 'yes' : '',
  };
}

/**
 * The map picture for a course or venue, for live previews: undefined while it
 * is being built or when it cannot be (the template then shows its styled
 * fallback). `course` = { routeLine, venue, venueName }.
 */
export function useCertificateMap(course) {
  const has = Boolean(coursePoints(course?.routeLine) || venuePoint(course?.venue));
  const query = useQuery({
    queryKey: ['certificateMap', JSON.stringify([coursePoints(course?.routeLine), venuePoint(course?.venue), course?.venueName || ''])],
    queryFn: () => loadCertificateMap(course),
    enabled: has,
    staleTime: Infinity,
    retry: 1,
  });
  return has ? query.data || '' : '';
}
